import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';
import { toast } from 'sonner';

export interface NewsCategory {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  created_at: string;
}

export interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  summary: string;
  original_url: string;
  source_name: string;
  source_logo: string | null;
  image_url: string | null;
  category_id: string | null;
  published_at: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  is_featured: boolean;
  category?: NewsCategory;
}

export function useNewsCategories() {
  return useQuery({
    queryKey: ['news-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as NewsCategory[];
    },
  });
}

export function useNewsArticles(options?: {
  category?: string;
  limit?: number;
  featured?: boolean;
}) {
  return useQuery({
    queryKey: ['news-articles', options],
    queryFn: async () => {
      let query = supabase
        .from('news_articles')
        .select(`
          *,
          category:news_categories(*)
        `);

      // For featured articles, order by display_order first, then published_at
      if (options?.featured) {
        query = query
          .eq('is_featured', true)
          .order('display_order', { ascending: true })
          .order('published_at', { ascending: false });
      } else {
        query = query.order('published_at', { ascending: false });
      }

      if (options?.category) {
        const { data: cat } = await supabase
          .from('news_categories')
          .select('id')
          .eq('slug', options.category)
          .maybeSingle();
        
        if (cat) {
          query = query.eq('category_id', cat.id);
        }
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as NewsArticle[];
    },
  });
}

export function useNewsArticle(slug: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['news-article', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select(`
          *,
          category:news_categories(*)
        `)
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Article not found');

      // Increment view count
      await supabase.rpc('increment_news_view', { p_id: data.id });

      return data as NewsArticle;
    },
    enabled: !!slug,
  });
}

export function useRelatedNews(articleId: string, categoryId: string | null) {
  return useQuery({
    queryKey: ['related-news', articleId, categoryId],
    queryFn: async () => {
      let query = supabase
        .from('news_articles')
        .select(`
          *,
          category:news_categories(*)
        `)
        .neq('id', articleId)
        .order('published_at', { ascending: false })
        .limit(4);

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as NewsArticle[];
    },
    enabled: !!articleId,
  });
}

export function useRealtimeNews() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel('news-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'news_articles',
        },
        (payload) => {
          // Realtime update received
          toast.info('New AI news just arrived!', {
            description: (payload.new as NewsArticle).title,
            action: {
              label: 'View',
              onClick: () => {
                window.location.href = `/news/${(payload.new as NewsArticle).slug}`;
              },
            },
          });
          
          // Invalidate queries to refresh the list
          queryClient.invalidateQueries({ queryKey: ['news-articles'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}

export function useFetchNews() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('fetch-ai-news');
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['news-articles'] });
      toast.success(data.message || 'News fetched successfully');
    },
    onError: (error) => {
      toast.error('Failed to fetch news', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}
